<script lang="ts">
/**
 * How much room the field takes, and at what size — the two things every
 * caller used to decide for itself. Nine different hardcoded widths were in
 * use before this prop existed.
 */
export type GaiaSearchInputVariant = 'toolbar' | 'panel' | 'fill' | 'hero';
</script>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, type PropType } from 'vue';
import { inputProps, type InputInst } from 'naive-ui';
import { IconSearch } from '@/components/design/icons';
import { gaiaHeroSearchOverrides } from '../../themes/tokens';

const props = defineProps({
  ...inputProps,
  /** Footprint and size. See GaiaSearchInputVariant. */
  variant: {
    type: String as PropType<GaiaSearchInputVariant>,
    default: 'toolbar',
  },
  /**
   * Milliseconds to wait after the last keystroke before `search` fires. Pass
   * 0 to make it immediate — for a caller that filters an in-memory list and
   * has nothing to wait for.
   */
  debounce: {
    type: Number,
    default: 300,
  },
  placeholder: {
    type: String,
    default: 'Search',
  },
  // A search field that can't be emptied in one click is a search field the
  // user has to backspace out of.
  clearable: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits<{
  /**
   * The debounced term — the hook to fetch on. Fires immediately (cancelling
   * anything pending) when the field is cleared or Enter is pressed, so a
   * cleared list repopulates at once instead of a debounce later.
   */
  search: [value: string];
  /** Enter. For the toolbars that used to sit a search button beside the field. */
  submit: [value: string];
}>();

const VARIANT_SIZE: Record<GaiaSearchInputVariant, 'small' | 'medium' | 'large'> = {
  toolbar: 'medium',
  panel: 'small',
  fill: 'medium',
  hero: 'large',
};

const inputRef = ref<InputInst | null>(null);

/**
 * `variant` and `debounce` are ours, not naive's, so they have to come out
 * before the spread — `v-bind`ing them onto n-input would land them on the DOM
 * element as stray attributes. `size`, `round` and `themeOverrides` are
 * re-derived from the variant, with an explicit prop from the caller still
 * winning: a toolbar that is genuinely tighter than the default keeps its own.
 */
const inputBindings = computed<Record<string, unknown>>(() => {
  const bindings: Record<string, unknown> = { ...props };
  delete bindings.variant;
  delete bindings.debounce;
  delete bindings.onKeydown;
  bindings.size = props.size ?? VARIANT_SIZE[props.variant];
  bindings.round = props.round || props.variant === 'hero';
  bindings.themeOverrides =
    props.themeOverrides ?? (props.variant === 'hero' ? gaiaHeroSearchOverrides : undefined);
  return bindings;
});

/**
 * The debounce is a plain timer rather than `useDebounceFn`, which the rest of
 * the app uses: clearing the field and pressing Enter both have to *cancel* a
 * pending call and emit at once, and vueuse's wrapper exposes no cancel — so a
 * clear would fire once with '' and then again, a debounce later, with the
 * text that was there before it.
 */
let timer: ReturnType<typeof setTimeout> | undefined;

function cancelPending(): void {
  if (timer !== undefined) {
    clearTimeout(timer);
    timer = undefined;
  }
}

function flush(value: string): void {
  cancelPending();
  emit('search', value);
}

function schedule(value: string): void {
  cancelPending();
  if (props.debounce <= 0) {
    emit('search', value);
    return;
  }
  timer = setTimeout(() => {
    timer = undefined;
    emit('search', value);
  }, props.debounce);
}

onBeforeUnmount(cancelPending);

/**
 * The value and clear handlers sit alongside the forwarded `onUpdateValue` /
 * `onUpdate:value` / `onClear` rather than replacing them: Vue's `mergeProps`
 * concatenates same-named `on*` keys from a `v-bind` object with the template's
 * own listeners, naive declares those three as `[Function, Array]` and calls
 * every entry — so a caller's `@update:value` still fires and `v-model` still
 * works.
 */
function handleUpdateValue(value: string): void {
  schedule(value);
}

function handleClear(): void {
  flush('');
}

/**
 * Keydown is the exception, and so is forwarded by hand: naive types it as a
 * bare `Function` rather than `[Function, Array]`, so letting a caller's
 * `@keydown` merge with ours would hand it an array and trip prop validation.
 */
const callerKeydown = computed(() => props.onKeydown);

function handleKeydown(event: KeyboardEvent): void {
  callerKeydown.value?.(event);
  if (event.key !== 'Enter') return;
  // Read the element, not `props.value`, so an uncontrolled field (defaultValue
  // with no bound value) submits what the user actually typed.
  const target = event.target;
  const value = target instanceof HTMLInputElement ? target.value : String(props.value ?? '');
  flush(value);
  emit('submit', value);
}

/**
 * Same reason GaiaInput forwards these — a `<script setup>` component is
 * closed, so a caller holding a ref to this wrapper can't reach the n-input
 * inside it. The command palette focuses its field on open.
 *
 * `cancel` drops a search still debouncing — for a caller that sets the value
 * itself, which never reaches the timer.
 */
defineExpose({
  focus: (): void => inputRef.value?.focus(),
  blur: (): void => inputRef.value?.blur(),
  select: (): void => inputRef.value?.select(),
  cancel: cancelPending,
});
</script>

<template>
  <n-input
    ref="inputRef"
    v-bind="inputBindings"
    :class="`gaia-search-input gaia-search-input--${props.variant}`"
    @update:value="handleUpdateValue"
    @clear="handleClear"
    @keydown="handleKeydown"
  >
    <template #prefix>
      <n-icon :component="IconSearch" />
    </template>
    <template v-if="$slots.suffix" #suffix>
      <slot name="suffix" />
    </template>
  </n-input>
</template>

<style scoped>
/* Variant geometry. naive's Input theme sizes the control but has no width or
   flex var, so the field's footprint can only be expressed here.

   Classes rather than an inline style, deliberately: a caller's own `:style`
   still wins (a few toolbars are genuinely tighter than the default), and the
   collapsed sidebar rail hides this field with
   `.gaia-sidebar--collapsed .top :deep(.n-input) { display: none }` — a rule an
   inline layout style would defeat. Nothing here sets `display` for the same
   reason. */
.gaia-search-input--toolbar {
  width: 264px;
  flex: 0 0 auto;
}

.gaia-search-input--panel {
  width: 100%;
}

.gaia-search-input--fill {
  flex: 1 1 auto;
  min-width: 0;
}

/* The field that carries the whole toolbar: narrow at rest, widening while it
   has focus. A transition on flex-basis, which no theme var can express. */
.gaia-search-input--hero {
  flex: 0 1 280px;
  transition: flex-basis 0.25s ease;
}

.gaia-search-input--hero:focus-within {
  flex-basis: 420px;
}
</style>
